/* Single axios instance for the live TISMS REST API.
   - baseURL = TISMS_API_URL, JSON headers
   - request interceptor attaches `Authorization: Bearer <accessToken>`
   - response interceptor: on 401, refreshes the session once and retries;
     only if the refresh is refused does it sign out, saying why
   Envelope: success → { data: <payload> }, failure → { error: {...} }. */
import axios from 'axios';
import type { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { TISMS_API_URL } from '../const';
import {
  getAccessToken,
  getRefreshToken,
  handleRefreshed,
  handleUnauthorized,
  setAccessToken,
  setRefreshToken,
} from './tokenHolder';

export interface ApiEnvelope<T> {
  data?: T;
  error?: { code?: string; message?: string; [k: string]: unknown };
}

export class ApiError extends Error {
  code?: string;
  status?: number;
  constructor(message: string, code?: string, status?: number) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
  }
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: TISMS_API_URL,
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
  timeout: 30000,
});

apiClient.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers = config.headers ?? {};
    (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
  }
  return config;
});

/* Access tokens live 15 minutes. There was no refresh: the first 401 after
   that signed the user out, so the web asked everyone to sign in again every
   quarter of an hour. Now a 401 refreshes once — one refresh shared by every
   request that failed at the same moment — and retries. Only a refused
   refresh ends the session, and the sign-in page is told why. */
let refreshing: Promise<string> | null = null;

const NO_REFRESH = ['auth/login', 'auth/refresh', 'auth/password'];

const refreshSession = async (): Promise<string> => {
  const rt = getRefreshToken();
  if (!rt) throw new Error('NO_REFRESH_TOKEN');
  // Plain axios, not apiClient: a refresh must not go through this interceptor.
  const res = await axios.post(
    `${TISMS_API_URL.replace(/\/$/, '')}/auth/refresh`,
    { refreshToken: rt },
    { headers: { 'Content-Type': 'application/json' }, timeout: 20000 },
  );
  const d = res.data?.data ?? {};
  setAccessToken(d.accessToken);
  if (d.refreshToken) setRefreshToken(d.refreshToken);
  handleRefreshed({
    accessToken: d.accessToken,
    refreshToken: d.refreshToken ?? rt,
    mustChangePassword: d.mustChangePassword === true,
  });
  return d.accessToken as string;
};

apiClient.interceptors.response.use(
  (res) => res,
  async (error) => {
    const status: number | undefined = error?.response?.status;
    const config = error?.config as (InternalAxiosRequestConfig & { _retried?: boolean }) | undefined;
    const url = String(config?.url ?? '');
    if (status !== 401 || !config || config._retried || NO_REFRESH.some((p) => url.includes(p))) {
      return Promise.reject(error);
    }
    try {
      refreshing = refreshing ?? refreshSession().finally(() => {
        refreshing = null;
      });
      const token = await refreshing;
      config._retried = true;
      config.headers = config.headers ?? {};
      (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
      return apiClient(config);
    } catch (refreshError) {
      const code = (refreshError as { response?: { data?: { error?: { code?: string } } } })?.response?.data?.error?.code;
      handleUnauthorized(
        code === 'PASSWORD_CHANGED'
          ? { code, message: 'Your password was changed on another device. Sign in with the new password.' }
          : { code: 'SESSION_ENDED', message: 'Your session has ended. Please sign in again.' },
      );
      return Promise.reject(error);
    }
  },
);

/** Unwrap `{ data }`, throw an ApiError on `{ error }` or transport failure. */
export const unwrap = <T>(res: AxiosResponse<ApiEnvelope<T>>): T => {
  const body = res.data;
  if (body && body.error) {
    throw new ApiError(body.error.message ?? 'Request failed', body.error.code, res.status);
  }
  if (!body || body.data === undefined) {
    throw new ApiError('Malformed response — missing data', 'MALFORMED', res.status);
  }
  return body.data;
};

/** Typed GET helper that unwraps the envelope. */
export const apiGet = async <T>(url: string, params?: Record<string, unknown>): Promise<T> => {
  try {
    const res = await apiClient.get<ApiEnvelope<T>>(url, { params });
    return unwrap<T>(res);
  } catch (e) {
    throw toApiError(e);
  }
};

/** Typed POST helper that unwraps the envelope. */
export const apiPost = async <T>(url: string, data?: unknown): Promise<T> => {
  try {
    const res = await apiClient.post<ApiEnvelope<T>>(url, data);
    return unwrap<T>(res);
  } catch (e) {
    throw toApiError(e);
  }
};

/** Normalise any thrown value (axios error / envelope error) into an ApiError. */
export const toApiError = (e: unknown): ApiError => {
  if (e instanceof ApiError) return e;
  const err = e as {
    response?: { status?: number; data?: { error?: { code?: string; message?: string } } };
    message?: string;
  };
  const envErr = err?.response?.data?.error;
  if (envErr) return new ApiError(envErr.message ?? 'Request failed', envErr.code, err.response?.status);
  const status = err?.response?.status;
  if (status === 401) return new ApiError('Invalid credentials or session expired', 'UNAUTHORIZED', 401);
  return new ApiError(err?.message ?? 'Network error', 'NETWORK', status);
};
